import type {
  EnforcementStatus,
  NativePolicyEffect,
} from "@/api/temp/AgentEstateService";

/**
 * How one Intent Policy is realized at each enforcement point it maps to.
 *
 * This is the relationship data the Enforcement view needs from a backend:
 * which MCP server policies, tool policies and data-source controls carry the
 * intent. It says nothing about which agents the policy is effective for -
 * that comes from agent assignments, so an apply is reflected everywhere.
 */
export interface IntentPolicyDefinition {
  id: string;
  name: string;
  description: string;
  allow: string[];
  restrict: string[];
  /** Agent `demoKey`s the policy is eligible for. */
  agentKeys: string[];
  mcp: Record<
    string,
    {
      serverPolicyName: string;
      registryServerId?: string;
      /** Tool-specific rules; every other tool inherits the server policy. */
      toolOverrides: Record<string, { rule: string; status?: EnforcementStatus }>;
    }
  >;
  data: Record<
    string,
    {
      controlIds: string[];
      /** Per-agent mapping gaps, keyed by agent `demoKey`. */
      exceptions?: Array<{
        agentKey: string;
        controlId: string;
        status: EnforcementStatus;
        detail: string;
      }>;
    }
  >;
}

/**
 * `effect` and `subject` are the control's semantic logic - what the native
 * data-platform policy generated from it says - so a manual change to it can
 * be shown as "BLOCK Finance Data" against "ALLOW Finance Data".
 */
export const DATA_CONTROLS: Record<
  string,
  {
    name: string;
    description: string;
    effect: NativePolicyEffect;
    subject: string;
  }
> = {
  "customer-support-access": {
    name: "Customer Support Access",
    description: "Read access limited to support cases and ticket data.",
    effect: "ALLOW",
    subject: "Customer Support Data",
  },
  "finance-restriction": {
    name: "Finance Restriction",
    description: "Finance, Trading and Employee Data are denied.",
    effect: "BLOCK",
    subject: "Finance Data",
  },
  "pii-redaction": {
    name: "PII Redaction",
    description: "Customer-identifying fields are masked in results.",
    effect: "MASK",
    subject: "Customer PII",
  },
};

const SERVER_POLICY = "Customer Support Server Access Policy";
const READ_ONLY_RULE = "Restricted to support case records; writes need approval";

/**
 * Keeps the recommendation's id, so the recommendation an administrator
 * applies and the Intent Policy the Enforcement view shows are one record.
 */
export const CUSTOMER_SUPPORT_DATA_ACCESS: IntentPolicyDefinition = {
  id: "demo-support-service-data",
  name: "Customer Support Data Access",
  description:
    "Customer support agents may use customer support and ticket data, and nothing outside it.",
  allow: ["Customer Support Data", "Ticket Data"],
  restrict: ["Finance", "Trading", "Employee Data"],
  agentKeys: [
    "customer-support",
    "service-desk",
    "ticket-resolution",
    "customer-insights",
    "client-retention-insights",
  ],
  mcp: {
    "mcp-sharepoint-support": {
      serverPolicyName: SERVER_POLICY,
      toolOverrides: {
        uploadCaseAttachment: { rule: READ_ONLY_RULE },
        shareDocumentLink: {
          rule: "Links shared only with the requesting customer's case",
        },
      },
    },
    "mcp-servicenow-support": {
      serverPolicyName: SERVER_POLICY,
      registryServerId: "ff8080819c907d1e019c91d2aa070005",
      toolOverrides: {
        updateIncident: { rule: READ_ONLY_RULE },
        addWorkNote: { rule: "Work notes limited to assigned tickets" },
      },
    },
    "mcp-salesforce-support": {
      serverPolicyName: SERVER_POLICY,
      registryServerId: "ff8080819c907d1e019c91d2aa070004",
      toolOverrides: {
        logInteraction: { rule: "Interactions logged against open cases only" },
      },
    },
  },
  data: {
    // Snowflake Production
    "8a4883a28b341a56018b3746fb710183": {
      controlIds: [
        "customer-support-access",
        "finance-restriction",
        "pii-redaction",
      ],
    },
    // Customer Data Platform
    "8a4881259d75a817019d760fe8d20811": {
      controlIds: ["customer-support-access", "pii-redaction"],
      exceptions: [
        {
          agentKey: "client-retention-insights",
          controlId: "pii-redaction",
          status: "NOT_MAPPED",
          detail:
            "No masking policy is mapped for this agent's analytics identity.",
        },
      ],
    },
  },
};
